import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"
import type {
  Acquirer,
  Source,
} from "../sharedTypes/merchants"
import type { MerchantCurrencyCode } from "@/utils/currency"

export type MerchantDetail = {
  id: string
  businessName: string

  tpReference: string
  merchantCategoryCode: string

  customerName: string
  customerEmail: string
  customerPhone: string

  addressLine1: string | null
  addressLine2: string | null

  cardstreamMerchantId: string
  cardstreamMerchantSecret: string

  acquirer: Acquirer
  paxMerchantId: string
  evoConfig: {
    gratuityIsAvailable: boolean
  }

  hasSalesModule: boolean
  hasDonationsModule: boolean
  hasBeepaidOrderModule: boolean
  hasBeepaidGoModule: boolean

  isFromBackbook: boolean
  isDemoMerchant: boolean
  isActive: boolean

  transactionTotal: number
  transactionCount: number
  createdAt: Date | null
  firstTransactedAt: Date | null
  lastTransactedAt: Date | null
  portalLink: string
  merchantGroup: {
    name: string
    id: string
  } | null
  sources: Source[] | null

  isVatEnabled: boolean
  vatNumber: string
  isCashEnabled: boolean
  isGratuityEnabled: boolean

  isUnlinkedRefundsEnabled: boolean

  isProductsEnabled: boolean
  isCashiersEnabled: boolean
  isServiceChargeEnabled: boolean
  isSplitPaymentsEnabled: boolean

  transactionCountsByWeekday: {
    dayOfWeek: 1 | 2 | 3 | 4 | 5 | 6 | 7
    transactionCount: number
  }[]
  transactionCountsByWeekdayLastWeek: {
    dayOfWeek: 1 | 2 | 3 | 4 | 5 | 6 | 7
    transactionCount: number
  }[]

  merchantStatsRefreshedAt: Date
  currency: MerchantCurrencyCode
}

export const retrieveMerchant = async (
  tenantId: number
): Promise<MerchantDetail> => {
  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<MerchantDetail>
  >(`/merchants/${tenantId}/`)

  return {
    ...body,
    createdAt: body.createdAt
      ? DateFns.parseISO(body.createdAt)
      : null,
    firstTransactedAt: body.firstTransactedAt
      ? DateFns.parseISO(body.firstTransactedAt)
      : null,
    lastTransactedAt: body.lastTransactedAt
      ? DateFns.parseISO(body.lastTransactedAt)
      : null,
    merchantStatsRefreshedAt: DateFns.parseISO(
      body.merchantStatsRefreshedAt
    ),
  }
}

export const useMerchant = (tenantId: MaybeRef<number>) =>
  useQuery({
    queryKey: [MERCHANTS_QUERY_KEY, tenantId] as const,
    queryFn: ({ queryKey: [_, id] }) =>
      retrieveMerchant(id),
  })
